import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowDown } from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";

const slides = [
  {
    id: 1,
    number: "01",
    title: "Sotsek Advocacia",
    description:
      "Escritório especializado em direito tributário, imobiliário e propriedade intelectual, atuando com rigor técnico, ética e foco total em resultados. Atendimento de excelência para oferecer segurança jurídica e apoiar clientes e parceiros em cada etapa.",
  },
  {
    id: 2,
    number: "02",
    title: "Sotsek Advocacia",
    description:
      "Liderado pela sócia-fundadora Luiza Mourad Sotsek, o escritório prioriza soluções inteligentes e rápidas, oferecendo respostas eficazes mesmo nos cenários mais complexos.",
  },
  {
    id: 3,
    number: "03",
    title: "Sotsek Advocacia",
    description:
      "Um escritório que valoriza ética e relações sólidas. Buscamos parcerias contínuas e confiáveis, mantendo postura proativa para estar sempre à frente.",
  },
];

export const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        <img
          src={heroBg}
          alt="Sotsek Advocacia - Escritório de advocacia especializado em direito tributário"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/20 to-background/80" />
      </motion.div>

      {/* Content */}
      <div className="relative h-full container mx-auto px-6 lg:px-12">
        <div className="h-full flex flex-col justify-between py-32 lg:py-40">
          {/* Slide Content */}
          <div className="flex-1 flex items-center">
            <div className="max-w-2xl">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentSlide}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Description */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-base lg:text-lg text-foreground/80 leading-relaxed font-body max-w-xl"
                  >
                    {slides[currentSlide].description}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="flex items-end justify-between">
            {/* Large Title */}
            <motion.h1
              initial={{ opacity: 0.8, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl sm:text-7xl lg:text-[10rem] xl:text-[12rem] font-light tracking-tight text-foreground leading-none"
            >
              <span className="text-primary">Sotsek</span> Advocacia
            </motion.h1>

            {/* Navigation Arrows */}
            <div className="hidden lg:flex flex-col gap-4 pb-8">
              <motion.button
                onClick={prevSlide}
                whileHover={{ scale: 1.1, x: -5 }}
                whileTap={{ scale: 0.95 }}
                className="w-12 h-12 rounded-full border border-foreground/30 flex items-center justify-center text-foreground/70 hover:border-primary hover:text-primary transition-colors"
                aria-label="Slide anterior"
              >
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              </motion.button>
              <motion.button
                onClick={nextSlide}
                whileHover={{ scale: 1.1, x: 5 }}
                whileTap={{ scale: 0.95 }}
                className="w-12 h-12 rounded-full border border-foreground/30 flex items-center justify-center text-foreground/70 hover:border-primary hover:text-primary transition-colors"
                aria-label="Próximo slide"
              >
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </motion.button>
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4" role="tablist" aria-label="Slides do carousel">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setDirection(index > currentSlide ? 1 : -1);
                  setCurrentSlide(index);
                }}
                className="relative w-6 h-6 flex items-center justify-center"
                role="tab"
                aria-selected={index === currentSlide}
                aria-label={`Ir para slide ${index + 1}`}
              >
                <span
                  className={`block rounded-full transition-all duration-300 ${
                    index === currentSlide
                      ? "w-8 h-2 bg-primary"
                      : "w-2 h-2 bg-foreground/30 hover:bg-foreground/50"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>

          {/* Scroll Indicator */}
          <motion.a
            href="#areas"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-2 text-foreground/50 hover:text-primary transition-colors"
          >
            <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowDown className="w-4 h-4" />
            </motion.div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};
